import {
  type blockInitialValues,
  createNewBlock,
} from '../../../initial-states/textBlocks.initialState';
import type { BlockType } from '../../../interfaces/textEditor.interface';

/**
 * Handles the change of a block's type within a collection of blocks.
 *
 * This function extracts the content from the current block based on its type,
 * creates a new block of the specified new type, and preserves the content in
 * the new block. It then updates the form values with the new block at the specified index.
 *
 * @param {number} index - The index of the block to be changed.
 * @param {BlockType} newType - The new type for the block.
 * @param {function} setFieldValue - A function to set the value of a field in the form.
 * @param {object} values - The current form values containing the blocks.
 */
export const handleBlockTypeChange = (
  index: number,
  newType: BlockType,
  setFieldValue: any,
  values: any,
) => {
  // Accept both Formik value shapes: object `{ blocks: TextBlock[] }`
  // (EditDraft / EditPostReplyDraft path) and flat `TextBlock[]` (PostDraft
  // add-post path). Pre-TRIL-204 this was a 3-way branch on the dead-set
  // `isEditingPost` and garbage-typed `addPost` Redux flags; both have been
  // dropped — the shape check is the actual signal.
  const blockSource: any[] = Array.isArray(values)
    ? values
    : (values?.blocks ?? []);
  const currentBlock = blockSource[index];

  let content = '';

  // Extract content from the current block based on its type
  if (currentBlock.type === 'Paragraph') {
    content = currentBlock.data?.paragraph || '';
  } else if (currentBlock.type === 'Heading') {
    content = currentBlock.data?.header || '';
  } else if (currentBlock.type === 'Code') {
    content = currentBlock.data?.code || '';
  } else if (currentBlock.type === 'Quote') {
    content = currentBlock.data?.paragraph || '';
  }

  // Create a new block with the new type
  const newBlock = createNewBlock(newType as keyof typeof blockInitialValues);

  // Preserve content in the appropriate field for the new block type
  if (newType === 'Paragraph') {
    newBlock.data.paragraph = content;
  } else if (newType === 'Heading') {
    newBlock.data.header = content;
    newBlock.data.level = 1; // Default to H1
  } else if (newType === 'Code') {
    newBlock.data.code = content;
  } else if (newType === 'Quote') {
    newBlock.data.paragraph = content;
  }
  // For List and Image, we're not transferring content as they have special formats

  // TRIL-214: every consumer of this handler is downstream of a Formik
  // <FieldArray name="blocks">, so the setFieldValue path is ALWAYS
  // `blocks.${index}` regardless of how `values` was drilled down to this
  // call site. The Array.isArray() shape check is correct for the READ
  // side above (currentBlock lookup), but the WRITE side is unconditional.
  // (Previously TRIL-204 split this on Array.isArray; the array branch
  // wrote to bare `${index}` which silently no-op'd the reply-from-feed
  // flow because RichEditorBody.tsx:149 prop-drills `values.blocks`.)
  setFieldValue(`blocks.${index}`, newBlock);
};
